/**
 * Subscription Service — Frontend service for payment & subscription
 * Follows brokerService.js pattern: fetch to Netlify functions, returns { success, data?, error? }
 */
import { supabase, isSupabaseConfigured } from './supabaseClient';

/**
 * Get all active subscription plans from DB (public RLS — no auth needed)
 */
export async function getSubscriptionPlans() {
  if (!isSupabaseConfigured()) {
    return { success: false, error: 'Supabase not configured', data: [] };
  }

  try {
    const { data, error } = await supabase
      .from('subscription_plans')
      .select('*')
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (err) {
    console.error('Error fetching subscription plans:', err);
    return { success: false, error: err.message, data: [] };
  }
}

/**
 * Check current user's subscription status
 */
export async function checkSubscription() {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) {
      return {
        success: true,
        data: { hasSubscription: false, subscriptions: [], features: [], currentPlan: 'free' },
      };
    }

    const response = await fetch('/.netlify/functions/check-subscription', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ userId: session.user.id }),
    });

    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Failed to check subscription');

    return { success: true, data: result };
  } catch (err) {
    console.error('Error checking subscription:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Create a Razorpay order for a plan
 */
export async function createOrder(planCode, couponCode) {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) return { success: false, error: 'Not authenticated' };

    const response = await fetch('/.netlify/functions/create-order', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        planCode,
        couponCode: couponCode || undefined,
        userId: session.user.id,
      }),
    });

    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Failed to create order');

    return { success: true, data: result };
  } catch (err) {
    console.error('Error creating order:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Verify Razorpay payment after checkout
 */
export async function verifyPayment(paymentData) {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) return { success: false, error: 'Not authenticated' };

    const response = await fetch('/.netlify/functions/verify-payment', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        ...paymentData,
        userId: session.user.id,
      }),
    });

    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Payment verification failed');

    return { success: true, data: result };
  } catch (err) {
    console.error('Error verifying payment:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Validate a coupon code
 */
export async function validateCoupon(code, planCode) {
  try {
    const response = await fetch('/.netlify/functions/validate-coupon', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, planCode }),
    });

    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Coupon validation failed');

    return { success: true, data: result };
  } catch (err) {
    console.error('Error validating coupon:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Dynamically load Razorpay checkout script
 */
export function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (document.getElementById('razorpay-script')) {
      resolve(true);
      return;
    }

    const script = document.createElement('script');
    script.id = 'razorpay-script';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

/**
 * Open Razorpay checkout modal
 */
export async function openRazorpayCheckout(orderData, userInfo, onSuccess, onFailure) {
  const loaded = await loadRazorpayScript();
  if (!loaded) {
    onFailure('Failed to load payment gateway');
    return;
  }

  const options = {
    key: orderData.keyId,
    amount: orderData.amountInPaise,
    currency: orderData.currency || 'INR',
    name: 'FINNCAL',
    description: `${orderData.planName} Subscription`,
    order_id: orderData.orderId,
    prefill: {
      name: userInfo.name || '',
      email: userInfo.email || '',
    },
    theme: {
      color: '#1e3a5f',
    },
    handler: function (response) {
      onSuccess({
        razorpay_order_id: response.razorpay_order_id,
        razorpay_payment_id: response.razorpay_payment_id,
        razorpay_signature: response.razorpay_signature,
        planCode: orderData.planCode,
        couponCode: orderData.couponCode,
      });
    },
    modal: {
      ondismiss: function () {
        onFailure('Payment cancelled');
      },
    },
  };

  const rzp = new window.Razorpay(options);
  rzp.on('payment.failed', function (response) {
    onFailure(response.error?.description || 'Payment failed');
  });
  rzp.open();
}
